import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, Lock, Mail } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth, formatApiError } from "../lib/auth";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const planParam = params.get("plan");
  const { login, user, hydrated } = useAuth();

  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // If already logged in, bounce to dashboard (or back to plan flow).
  useEffect(() => {
    if (hydrated && user) {
      navigate(planParam ? `/signup?plan=${planParam}` : "/dashboard", { replace: true });
    }
  }, [hydrated, user, navigate, planParam]);

  const submit = async (e) => {
    e?.preventDefault();
    const errs = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Enter a valid email";
    if (!form.password) errs.password = "Password is required";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setProcessing(true);
    try {
      await login({ email: form.email, password: form.password });
      navigate(planParam ? `/signup?plan=${planParam}` : "/dashboard", { replace: true });
    } catch (err) {
      setErrors({ general: formatApiError(err, "Could not sign in.") });
    } finally {
      setProcessing(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/dashboard${planParam ? `?plan=${planParam}` : ""}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 relative overflow-hidden" data-testid="login-page">
      <div className="absolute inset-0 bg-grid mask-radial-fade opacity-30 pointer-events-none" />
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-[#1D6AFF]/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] bg-[#1D6AFF]/8 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-md mx-auto px-5 pt-10 pb-16">
        <button
          data-testid="login-back"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to site
        </button>

        <div className="flex justify-center mb-7">
          <Logo size="lg" />
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl p-7 sm:p-8 animate-fade-up shadow-[0_20px_50px_-15px_rgba(15,23,42,0.12)]">
          <h1 className="font-display text-3xl font-bold mb-2 text-slate-900">Welcome back</h1>
          <p className="text-sm text-slate-500 mb-7">Sign in to your Newground account.</p>

          <button
            data-testid="google-login-btn"
            onClick={googleLogin}
            className="w-full flex items-center justify-center gap-3 bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 font-semibold rounded-xl py-3.5 transition-all mb-5"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
              <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.45c-.28 1.46-1.13 2.7-2.4 3.53v2.93h3.88c2.27-2.09 3.56-5.17 3.56-8.7z" />
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.93l-3.88-2.93c-1.07.73-2.45 1.16-4.05 1.16-3.11 0-5.75-2.1-6.69-4.93H1.3v3.09C3.28 21.31 7.31 24 12 24z" />
              <path fill="#FBBC05" d="M5.31 14.37c-.24-.73-.38-1.5-.38-2.37s.14-1.64.38-2.37V6.54H1.3C.47 8.18 0 10.04 0 12s.47 3.82 1.3 5.46l4.01-3.09z" />
              <path fill="#EA4335" d="M12 4.75c1.76 0 3.34.61 4.59 1.79l3.43-3.43C17.95 1.13 15.24 0 12 0 7.31 0 3.28 2.69 1.3 6.54l4.01 3.09C6.25 6.85 8.89 4.75 12 4.75z" />
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-500 uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <Field testId="login-email" icon={Mail} label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} error={errors.email} placeholder="jane@agency.com" />
            <Field
              testId="login-password"
              icon={Lock}
              label="Password"
              type={showPw ? "text" : "password"}
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              error={errors.password}
              placeholder="••••••••"
              rightAction={
                <button type="button" onClick={() => setShowPw(!showPw)} className="text-slate-400 hover:text-slate-700">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              }
            />
            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="login-error">{errors.general}</div>
            )}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={processing}
              className="w-full bg-[#1D6AFF] hover:bg-[#1552C6] disabled:opacity-70 text-white font-semibold rounded-xl py-3.5 transition-all shadow-[0_14px_30px_-8px_rgba(29,106,255,0.4)]"
            >
              {processing ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="text-center mt-4">
            <button
              data-testid="forgot-password-link"
              onClick={() => navigate("/forgot-password")}
              className="text-sm text-slate-500 hover:text-[#1D6AFF] font-medium"
            >
              Forgot your password?
            </button>
          </div>

          <div className="text-center text-sm text-slate-500 mt-6">
            New to Newground?{" "}
            <button
              data-testid="login-signup-link"
              onClick={() => navigate(planParam ? `/signup?plan=${planParam}` : "/signup")}
              className="text-[#1D6AFF] hover:underline font-medium"
            >
              Create an account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ testId, icon: Icon, label, value, onChange, type = "text", placeholder, error, rightAction }) => (
  <div>
    <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">{label}</label>
    <div className={`relative flex items-center bg-slate-50 border ${error ? "border-red-400" : "border-slate-200"} rounded-xl px-3.5 py-3 focus-within:border-[#1D6AFF] focus-within:bg-white transition-all`}>
      {Icon && <Icon className="w-4 h-4 text-slate-400 mr-2.5 shrink-0" />}
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent outline-none text-slate-900 placeholder:text-slate-400 text-sm"
      />
      {rightAction}
    </div>
    {error && <div className="text-xs text-red-500 mt-1.5">{error}</div>}
  </div>
);
